import { v4 as uuid } from 'uuid';
import { AiAgentConstants, AgentState } from '../aiAgent/AiAgentConstants';
import { groundingStatus, } from '../aiAgent/types';
import { role } from '../aiAgent/types/instructions';
import { ActionType } from '../models/Action';
import { StateViewModel } from '../models/viewModels/StateViewModel';
import { blipAiAssistantApi, defaultClusterName, shouldCheckAiAgentPlan } from 'app.constants';
import { CUSTOM_MCP_TOOL_PREFIX, getDefaultActionSchema, validateActionViewModelToActionTools } from '../aiAgent/utils/AgentUtils';
import { transportTypes } from '../aiAgent/types/tools';
import { ListApplicationFeatures } from 'modules/application/list/ListApplicationFeatures';
import { AVAILABLE_AGENT_MODELS, DEFAULT_PROVIDER, LEGACY_AGENT_MODELS, MODELMAP_PROVIDER } from '../aiAgent/utils/ModelUtils';
import { BuilderFeatures } from '../BuilderFeatures';
import { openAiInstructionToLangchainPrompt } from '../aiAgent/utils/promptUtils';
const cultures = ['en-US', 'pt-BR', 'es-MX'];
const getGuardRailsApiUrl = (culture) => {
    const mappedCulture = cultures.find(c => c.includes(culture)) ?? 'pt-BR';
    return blipAiAssistantApi + `/guardrail/instructions/general/${mappedCulture}`;
};
export class BuilderAiAgentService {
    static $inject = ['MessagingHubService', 'ContextProcessorService', '$http', 'CurrentApplicationService', 'BlipService'];
    static getIsNewModelsEnabled() {
        if (!BuilderAiAgentService.isNewModelsEnabledPromise) {
            BuilderAiAgentService.isNewModelsEnabledPromise = BuilderFeatures.isNewAiAgentLlmModelsEnabled();
        }
        return BuilderAiAgentService.isNewModelsEnabledPromise;
    }
    constructor(MessagingHubService, ContextProcessorService, $http, CurrentApplicationService, BlipService) {
        'ngInject';
        this.MessagingHubService = MessagingHubService;
        this.ContextProcessorService = ContextProcessorService;
        this.$http = $http;
        this.CurrentApplicationService = CurrentApplicationService;
        this.BlipService = BlipService;
    }
    //#region Helpers
    getTenantId() {
        return this.ContextProcessorService.tenant.id;
    }
    getApplication() {
        return this.CurrentApplicationService.fetchApplicationByStateParams();
    }
    static isAiAgentState(stateId) {
        return stateId?.startsWith(AiAgentConstants.STATE_ID_PREFIX);
    }
    isAiAgentStateInstance(stateId) {
        return BuilderAiAgentService.isAiAgentState(stateId);
    }
    async getAvailableModels() {
        const isNewModelsEnabled = await BuilderAiAgentService.getIsNewModelsEnabled();
        const currentCluster = defaultClusterName.toLowerCase();
        const isNewModelsEnabledForCluster = isNewModelsEnabled[currentCluster] ?? false;
        return isNewModelsEnabledForCluster ? AVAILABLE_AGENT_MODELS : LEGACY_AGENT_MODELS;
    }
    isAgentBlockOutdated(state) {
        return StateViewModel.getAiBlockVersion(state) < this.getAgentBlockLatestVersion();
    }
    /**
     * Retrieves or generates a unique toolkit code for the AI agent from state.
     * @param state The current state of the AI agent.
     * @returns The generated toolkit code.
     */
    fetchToolkitUUID(state) {
        const uuid = state.$toolActionsMapping?.builder?.uuid ?? this.generateUUID();
        return {
            uuid,
        };
    }
    generateUUID() {
        return uuid();
    }
    async retrieveCodeWithUUID(state, environment = AgentState.DEVELOPMENT) {
        const { uuid } = this.fetchToolkitUUID(state);
        const application = await this.getApplication();
        return {
            code: `${this.getTenantId()}-${application.shortName}-${state.id}-${environment}-${uuid}`,
            uuid: uuid,
        };
    }
    async retrieveGroundingCodeWithUUID(state, environment = AgentState.DEVELOPMENT) {
        const { uuid } = this.fetchToolkitUUID(state);
        const application = await this.getApplication();
        return {
            code: `${this.getTenantId()}-${application.shortName}-${state.id}-grounding-${environment}-${uuid}`,
            uuid: uuid,
        };
    }
    addMcpResourceToTools(generatedMCPResource, agentConfiguration) {
        agentConfiguration.tools = {
            ...agentConfiguration.tools,
            [generatedMCPResource.code]: {
                code: generatedMCPResource.code,
                mcp: generatedMCPResource.mcpUrl,
                transport: transportTypes.STREAMABLE_HTTP,
                headers: {
                    'X-Context-BotId': '{{application.node}}',
                    'X-Context-EndUserId': '{{contact.identity}}',
                },
            },
        };
    }
    removeMcpResourceFromTools(agentConfiguration, code) {
        if (agentConfiguration.tools && agentConfiguration.tools[code]) {
            delete agentConfiguration.tools[code];
        }
    }
    isAgentVersionUpToDate(state) {
        const agentSettings = state.$enteringCustomActions
            .find(action => action.type === ActionType.ForwardToAgent)
            .settings;
        return agentSettings.version === AiAgentConstants.CURRENT_AI_AGENTS_VERSION;
    }
    filterGrounding(groundingList, byStatus) {
        return groundingList.filter(item => item.status === byStatus);
    }
    /**
     * Maps AgentState to the environment string format used in filter IDs.
     * @param environment The agent environment.
     * @returns 'construction' for DEVELOPMENT, 'flow' for PUBLISHED.
     */
    mapEnvironmentToFilterString(environment) {
        return environment === AgentState.DEVELOPMENT ? 'construction' : 'flow';
    }
    //#endregion
    //#region Set Methods
    setAgentConfigurationOnState(agentConfiguration, state) {
        // eslint-disable-next-line no-console
        this.setProvider(agentConfiguration);
        state.$enteringCustomActions
            .find(action => action.type === ActionType.ForwardToAgent)
            .settings = agentConfiguration;
    }
    /**
     * Extracts local custom actions from the AI agent state and publishes them.
     * @param state The current state of the AI agent.
     * @param environment The environment in which the agent is running.
     * @returns A promise that resolves to an mpcResource or null if fails.
     */
    async extractAiAgentsLocalCustomActionsToPublish(state, environment = AgentState.DEVELOPMENT) {
        let tools = this.createActionTools(state);
        const { code, uuid } = (await this.retrieveCodeWithUUID(state, environment));
        const resource = {
            code,
            tools,
            environment,
            stateId: state.id,
        };
        const generatedMCPResource = await this.setToolsOnApa(resource, uuid);
        if (tools.length === 0) {
            return {
                mcpUrl: '',
                code: code,
                uuid: uuid,
                shouldDelete: true,
            };
        }
        return generatedMCPResource;
    }
    /**
     * Extracts local grounding custom actions from the AI agent state and publishes them.
     * @param state The current state of the AI agent.
     * @param environment The environment in which the agent is running.
     * @returns A promise that resolves to an mpcResource or null if fails.
     */
    async extractAiAgentsGroundingLocalCustomActionsToPublish(state, environment = AgentState.DEVELOPMENT) {
        const application = await this.getApplication();
        const tools = await this.createGroundingActionTools(state, environment, application.shortName);
        const { code, uuid } = (await this.retrieveGroundingCodeWithUUID(state, environment));
        const resource = {
            code,
            tools,
            environment,
            stateId: state.id,
        };
        const generatedMCPResource = await this.setGroundingToolsOnApa(resource, uuid);
        if (tools.length === 0) {
            return {
                mcpUrl: '',
                code: code,
                uuid: uuid,
                shouldDelete: true,
            };
        }
        return generatedMCPResource;
    }
    createActionTools(state) {
        const actionsViewModel = (state.$localCustomActions ?? []).filter(action => action.type !== ActionType.ForwardToAgent && action.type !== ActionType.KnowledgeBaseConsult);
        const actionTools = [];
        if (actionsViewModel.length === 0) {
            return actionTools;
        }
        actionsViewModel.forEach(action => {
            const { errors } = validateActionViewModelToActionTools(action, state);
            if (errors.length > 0) {
                console.warn(`Action ${action.$title} is not valid for action tools.` +
                    `\nErrors: ${JSON.stringify(errors)}`);
                return;
            }
            const actionTool = {
                name: action.$title,
                summary: action.$description,
                description: action.$description,
                inputSchema: action.$inputSchema ?? getDefaultActionSchema(),
                actionId: action.$id,
                context: {},
            };
            actionTools.push(actionTool);
        });
        // This method should create and return an array of action tools
        // For now, it returns an empty array as a placeholder
        return actionTools;
    }
    /**
     * Creates grounding action tools from KnowledgeBaseConsult actions.
     * @param state The current state view model.
     * @param environment The agent environment.
     * @param botId The bot identifier (shortName).
     * @returns An array of grounding action tools.
     */
    async createGroundingActionTools(state, environment, botId) {
        const groundingActions = (state.$localCustomActions ?? []).filter(action => action.type === ActionType.KnowledgeBaseConsult);
        if (groundingActions.length === 0) {
            return [];
        }
        const environmentString = this.mapEnvironmentToFilterString(environment);
        const groundingActionTools = [];
        groundingActions.forEach(action => {
            const { errors } = validateActionViewModelToActionTools(action, state);
            if (errors.length > 0) {
                console.warn(`Action ${action.$title} is not valid for grounding action tools.` +
                    `\nErrors: ${JSON.stringify(errors)}`);
                return;
            }
            const filterId = `${botId}|${state.id}|${environmentString}|${action.$id}`;
            const groundingTool = {
                name: action.$title,
                summary: action.$description,
                description: action.$description,
                filterId: filterId,
            };
            groundingActionTools.push(groundingTool);
        });
        return groundingActionTools;
    }
    setProvider(agentConfiguration) {
        agentConfiguration.model.provider = MODELMAP_PROVIDER[agentConfiguration?.model?.model] || DEFAULT_PROVIDER;
    }
    //#region Grounding Set
    async setAgentCatalogsOnApa(agentCatalogs, stateId, environment = AgentState.DEVELOPMENT) {
        try {
            const uri = `/knowledge-base/agent/${stateId}/environment/${environment}/setup`;
            return await this.MessagingHubService.processCommand({
                method: 'set',
                to: 'postmaster@aiplatform.msging.net',
                uri: uri,
                type: 'application/vnd.iris.aiplatform.knowledgebase-agent-setup+json',
                resource: { 'catalogs': agentCatalogs ?? [] },
            });
        }
        catch (error) {
            console.warn('Error setup catalogs to agents', error);
            return null;
        }
    }
    async insertGroundingMcpToAgentConfiguration(state, userEnvironment = 'construction') {
        const configuration = this.getFlowContent(state) ?? await this.getDefaultConfiguration();
        const host = AiAgentConstants.AI_AGENT_API_HOST;
        const environment = AiAgentConstants.AI_AGENT_API_HOST_ENVIRONMENT;
        const tenantId = this.getTenantId();
        const botId = '{{application.identifier}}';
        const stateId = '{{state.id}}';
        const mcp = `${host}/blip-document-server/${environment}/` +
            `${tenantId}/filter/${botId}|${stateId}|${userEnvironment}/mcp`;
        const subject = JSON.stringify({
            'roles': ['application'],
            'account_id': tenantId,
            'subscription_id': 200,
            'subscription_name': botId,
        });
        const headers = { subject };
        const groundingMcpTool = {
            mcp,
            headers,
            transport: transportTypes.STREAMABLE_HTTP,
            code: AiAgentConstants.DEFAULT_GROUNDING_MCP,
        };
        configuration.tools = {
            ...configuration.tools,
            [AiAgentConstants.DEFAULT_GROUNDING_MCP]: groundingMcpTool,
        };
    }
    async removeGroundingMcpFromAgentConfiguration(state) {
        const configuration = this.getFlowContent(state) ?? await this.getDefaultConfiguration();
        if (!this.hasGroundingMcp(configuration)) {
            return;
        }
        this.removeMcpResourceFromTools(configuration, AiAgentConstants.DEFAULT_GROUNDING_MCP);
    }
    hasGroundingMcp(agentConfiguration) {
        return agentConfiguration.tools &&
            Object.keys(agentConfiguration.tools).includes(AiAgentConstants.DEFAULT_GROUNDING_MCP);
    }
    /**
     * Normalize the inactive documents to minimal shape and forward them to Grounding APA.
     *
     * @param catalogId - ID of the catalog to update.
     * @param agentCatalogDocuments - Documents (full or minimal) to apply.
     * @param stateId - ID of the agent state that will receive the update.
     * @param environment - Target environment (defaults to AgentState.DEVELOPMENT).
     * @returns Promise resolving to the result returned by `setAgentDocumentsCatalogOnApa`.
     * @throws Any error raised by `setAgentDocumentsCatalogOnApa` is propagated to the caller.
     */
    async normalizeAndSetDocuments(catalogId, agentCatalogDocuments, stateId, environment = AgentState.DEVELOPMENT) {
        const minimalDocumentInfo = agentCatalogDocuments.map((doc) => ({
            id: doc.id,
            catalog_id: doc.catalog_id,
            status: doc.status,
        }));
        return this.setAgentDocumentsCatalogOnApa(catalogId, minimalDocumentInfo, stateId, environment);
    }
    async setAgentDocumentsCatalogOnApa(catalogId, agentCatalogDocuments, stateId, environment = AgentState.DEVELOPMENT) {
        try {
            const uri = `/knowledge-base/agent/${stateId}/environment/${environment}/setup`;
            return await this.MessagingHubService.processCommand({
                method: 'set',
                to: 'postmaster@aiplatform.msging.net',
                uri: uri,
                type: 'application/vnd.iris.aiplatform.knowledgebase-agent-setup+json',
                resource: { 'catalog_id': catalogId, 'documents': agentCatalogDocuments ?? [] },
            });
        }
        catch (error) {
            console.warn('Error setup catalogs to agents', error);
            return null;
        }
    }
    //#endregion
    //#region Tools Set
    /**
     * Migrates grounding tools for all AI agents by invoking the grounding migration endpoint.
     */
    async migrateGroundingTools() {
        try {
            let uri = `/knowledge-base/migrate`;
            const result = await this.MessagingHubService.processCommand({
                method: 'set',
                to: 'postmaster@aiplatform.msging.net',
                uri: uri,
                type: 'application/vnd.iris.aiplatform.grounding-migration+json',
                resource: {}
            });
            if (result.status === 'failure') {
                console.warn('Error setting grounding migration', result.reason.description);
                throw new Error(result.reason.description);
            }
        }
        catch (error) {
            console.warn('Error setting grounding migration', error);
            throw error;
        }
    }
    /**
     * Sets up the agent grounding tool with specified catalogs and documents.
     * @param stateId The ID of the agent state.
     * @param toolId The ID of the tool to set up.
     * @param chunkSize The chunk size for the tool setup.
     * @param catalogs The catalogs to include in the setup.
     * @param documents The documents to include in the setup.
     * @param environment The environment in which the agent is running.
     * @returns A promise that resolves to the tool setup, or null if an error occurs.
     */
    async setAgentGroundingToolSetup(stateId, toolId, chunkSize, catalogs, documents, environment = AgentState.DEVELOPMENT) {
        try {
            const uri = `/knowledge-base/agent/${stateId}/tool/${toolId}/environment/${environment}/setup`;
            return await this.MessagingHubService.processCommand({
                method: 'set',
                to: 'postmaster@aiplatform.msging.net',
                uri: uri,
                type: 'application/vnd.iris.aiplatform.knowledgebase-agent-setup+json',
                resource: {
                    top_k: chunkSize,
                    catalogs,
                    documents: documents.map(doc => ({ id: doc.id, catalog_id: doc.catalog_id, status: doc.status }))
                }
            });
        }
        catch (error) {
            console.warn('Error setting up tool for agents', error);
            return null;
        }
    }
    /**
     * Updates the MCP tool configuration based on the presence of active documents in the provided agent catalogs.
     * @param state Current state of the AI agent
     * @param agentCatalogs Active agent catalogs
     * @returns void
     */
    async updateMcpToolConfiguration(state, agentCatalogs) {
        for (const catalog of agentCatalogs) {
            const documents = await this.getCatalogDocuments(catalog.id, state.id, AgentState.DEVELOPMENT);
            const hasActiveDocuments = documents?.some(doc => doc.status === groundingStatus.ACTIVE) ?? false;
            if (hasActiveDocuments) {
                this.insertGroundingMcpToAgentConfiguration(state);
                return;
            }
        }
        this.removeGroundingMcpFromAgentConfiguration(state);
    }
    async setToolsOnApa(resource, uuid) {
        const uri = '/mcr/tools';
        const AGENT_TOOLS_TYPE = 'application/vnd.iris.aiplatform.set-agent-tools+json';
        try {
            const command = {
                method: 'set',
                to: 'postmaster@aiplatform.msging.net',
                uri: uri,
                type: AGENT_TOOLS_TYPE,
                resource: resource,
            };
            const result = await this.MessagingHubService.processCommand(command);
            if (result.status === 'failure') {
                console.warn('Failed to set tools', result);
                return null;
            }
            return {
                mcpUrl: result.resource.mcpUrl,
                code: resource.code,
                uuid: uuid,
            };
        }
        catch (error) {
            console.warn('Error setting tools on APA:', error);
            return null;
        }
    }
    async setGroundingToolsOnApa(resource, uuid) {
        const uri = '/mcr/tools/grounding';
        const AGENT_GROUNDING_TOOLS_TYPE = 'application/vnd.iris.aiplatform.set-agent-grounding-tools+json';
        try {
            const command = {
                method: 'set',
                to: 'postmaster@aiplatform.msging.net',
                uri: uri,
                type: AGENT_GROUNDING_TOOLS_TYPE,
                resource: resource,
            };
            const result = await this.MessagingHubService.processCommand(command);
            if (result.status === 'failure') {
                console.warn('Failed to set grounding tools', result);
                return null;
            }
            return {
                mcpUrl: result.resource.mcpUrl,
                code: resource.code,
                uuid: uuid,
            };
        }
        catch (error) {
            console.warn('Error setting grounding tools on APA:', error);
            return null;
        }
    }
    manipulateCommandReceiverOnApplicationJson(application, flowModel, isAiAgentCreateBuilderBlockEnabled) {
        if (!flowModel.states.some(state => this.isAiAgentStateInstance(state.id))) {
            return;
        }
        // Validate if the application contains the command receiver to add the single action command receiver
        // This receiver will allow MCR application to invoke actions defined in the flow
        // configure feature flag to only create the receiver in case AI Agent block is enabled
        if (isAiAgentCreateBuilderBlockEnabled) {
            application.applicationJson.commandReceivers = [{
                    state: 'default',
                    type: 'SingleActionCommandReceiver',
                    uri: '^/single-action-execution$',
                    sender: AiAgentConstants.AI_AGENT_IDENTITY_SINGLE_ACTION,
                }];
        }
    }
    //#endregion
    //#region Get Methods
    getAgentStates(flow) {
        return Object.values(flow).filter(state => this.isAiAgentStateInstance(state.id));
    }
    async getAgentConfiguration(state) {
        let flowContent = { ...(await this.getDefaultConfiguration()), ...this.getFlowContent(state) };
        flowContent.prompt = openAiInstructionToLangchainPrompt(flowContent.prompt);
        return flowContent;
    }
    getFlowContent(state) {
        // Navigate through the state to find the ForwardToAgent action and getting the settings object
        const aiAgentSettings = state.$enteringCustomActions
            .find(action => action.type === ActionType.ForwardToAgent)
            ?.settings;
        if (!aiAgentSettings || Object.keys(aiAgentSettings).length === 0) {
            return null;
        }
        return aiAgentSettings;
    }
    getAgentBlockLatestVersion() {
        if (!this.latestVersion) {
            this.latestVersion = "1.0.1";
        }
        return this.latestVersion;
    }
    async getDefaultConfiguration(shouldSetVersion = false) {
        // In a first interaction, the agent configuration is not set, so we need to show default values
        const availableModels = await this.getAvailableModels();
        const isStudioGroundingToolsMigrationEnabled = await BuilderFeatures.isStudioGroundingToolsMigrationEnabled();
        return {
            model: {
                provider: MODELMAP_PROVIDER[availableModels?.[0]] ?? DEFAULT_PROVIDER,
                model: availableModels?.[0] ?? '',
                maxTokens: 2048,
                temperature: 0.7,
            },
            prompt: [
                {
                    type: role.system,
                    content: '',
                },
                {
                    type: role.shortTermMemory,
                    config: {
                        length: 50,
                    },
                },
            ],
            handoffs: [],
            input: {
                audio: {
                    enableInputForwarding: false,
                },
                pdf: {
                    enableInputForwarding: false,
                },
                image: {
                    enableInputForwarding: false,
                },
                unsupportedContent: {
                    enableInputForwarding: false,
                },
            },
            output: {
                forward: {
                    enabled: true,
                    outputVariable: null,
                    handoffName: null,
                },
                contentTypes: {
                    text: {
                        enabled: true,
                    },
                    image: {
                        enabled: false,
                    },
                    webLink: {
                        enabled: false,
                    },
                    quickReply: {
                        enabled: false,
                    },
                    custom: {
                        enabled: false,
                        schema: {},
                    },
                },
            },
            tools: {},
            ...(isStudioGroundingToolsMigrationEnabled && shouldSetVersion && { version: AiAgentConstants.CURRENT_AI_AGENTS_VERSION })
        };
    }
    async getCatalogs(stateId, environment = AgentState.DEVELOPMENT) {
        try {
            const uri = `/knowledge-base/agent/${stateId}/environment/${environment}/catalog`;
            const responseCommand = await this.MessagingHubService.processCommand({
                method: 'get',
                to: 'postmaster@aiplatform.msging.net',
                uri: uri,
            });
            if (responseCommand.status === 'failure' || (responseCommand.status === 'success' && Object.keys(responseCommand.resource).length === 0)) {
                return null;
            }
            return responseCommand.resource.items;
        }
        catch (error) {
            console.warn('Error retrieving agent configuration:', error);
            return null;
        }
    }
    async getCatalogDocuments(catalogId, stateId, environment = AgentState.DEVELOPMENT) {
        try {
            const uri = `/knowledge-base/agent/${stateId}/environment/${environment}/catalog/${catalogId}/document`;
            const responseCommand = await this.MessagingHubService.processCommand({
                method: 'get',
                to: 'postmaster@aiplatform.msging.net',
                uri: uri,
            });
            if (responseCommand.status === 'failure' || (responseCommand.status === 'success' && Object.keys(responseCommand.resource).length === 0)) {
                return null;
            }
            return responseCommand.resource.items;
        }
        catch (error) {
            console.warn('Error retrieving agent configuration:', error);
            return null;
        }
    }
    async getCatalogsByToolId(stateId, toolId, environment = AgentState.DEVELOPMENT) {
        try {
            const uri = `/knowledge-base/agent/${stateId}/tool/${toolId}/environment/${environment}/catalog`;
            const responseCommand = await this.MessagingHubService.processCommand({
                method: 'get',
                to: 'postmaster@aiplatform.msging.net',
                uri: uri,
            });
            if (responseCommand.status === 'failure' || (responseCommand.status === 'success' && Object.keys(responseCommand.resource).length === 0)) {
                return null;
            }
            return responseCommand.resource.items;
        }
        catch (error) {
            console.warn(`Error retrieving catalogs for tool ${toolId} in state ${stateId}:`, error);
            return null;
        }
    }
    async getCatalogDocumentsByToolId(catalogId, stateId, toolId, environment = AgentState.DEVELOPMENT) {
        try {
            const uri = `/knowledge-base/agent/${stateId}/tool/${toolId}/environment/${environment}/catalog/${catalogId}/document`;
            const responseCommand = await this.MessagingHubService.processCommand({
                method: 'get',
                to: 'postmaster@aiplatform.msging.net',
                uri: uri,
            });
            if (responseCommand.status === 'failure' || (responseCommand.status === 'success' && Object.keys(responseCommand.resource).length === 0)) {
                return null;
            }
            return responseCommand.resource.items;
        }
        catch (error) {
            console.warn('Error retrieving documents by tool ID:', error);
            return null;
        }
    }
    async getGuardrailsTemplates(currentCulture) {
        const processRequest = (culture) => {
            return this.$http.get(getGuardRailsApiUrl(culture), {
                headers: {
                    Authorization: undefined,
                },
            });
        };
        try {
            const response = await processRequest(currentCulture);
            return response.data;
        }
        catch (error) {
            console.warn('Error retrieving guardrails templates:', error);
            return null;
        }
    }
    async getCatalogsWithDocuments(stateId, environment = AgentState.DEVELOPMENT) {
        const catalogs = await this.getCatalogs(stateId, environment);
        if (!catalogs) {
            return [];
        }
        const catalogDocumentsPromises = this.filterGrounding(catalogs, groundingStatus.ACTIVE).map(async (catalog) => {
            const documents = await this.getCatalogDocuments(catalog.id, stateId, environment);
            return {
                id: catalog.id,
                status: catalog.status,
                excluded_documents: this.filterGrounding(documents || [], groundingStatus.INACTIVE)
                    .map(({ id, catalog_id }) => ({
                    id, catalog_id
                }))
            };
        });
        return Promise.all(catalogDocumentsPromises);
    }
    /**
    * Retrieves the configuration of the agent grounding tool.
    * @param stateId The ID of the agent state.
    * @param toolId The ID of the tool to retrieve the configuration for.
    * @param environment The environment in which the agent is running.
    * @returns A promise that resolves to the tool configuration.
    */
    async getAgentGroundingToolSetup(stateId, toolId, environment = AgentState.DEVELOPMENT) {
        try {
            const uri = `/knowledge-base/agent/${stateId}/tool/${toolId}/environment/${environment}/setup`;
            const responseCommand = await this.MessagingHubService.processCommand({
                method: 'get',
                to: 'postmaster@aiplatform.msging.net',
                uri: uri,
            });
            if (responseCommand.status === 'failure' || (responseCommand.status === 'success' && Object.keys(responseCommand.resource).length === 0)) {
                return null;
            }
            return responseCommand.resource;
        }
        catch (error) {
            console.warn('Error retrieving tool configuration:', error);
            return null;
        }
    }
    /**
     * Retrieves the filter configuration of the agent grounding tool.
     * @param stateId The ID of the agent state.
     * @param toolId The ID of the tool to retrieve the filter for.
     * @param environment The environment in which the agent is running.
     * @returns A promise that resolves to the tool filter configuration, or null if an error occurs or the resource is empty.
     */
    async getAgentGroundingFilter(stateId, toolId, environment = AgentState.DEVELOPMENT) {
        try {
            const uri = `/knowledge-base/agent/${stateId}/tool/${toolId}/environment/${environment}/filter`;
            const responseCommand = await this.MessagingHubService.processCommand({
                method: 'get',
                to: 'postmaster@aiplatform.msging.net',
                uri: uri,
            });
            if (responseCommand.status === 'failure' || (responseCommand.status === 'success' && Object.keys(responseCommand.resource).length === 0)) {
                return null;
            }
            return responseCommand.resource;
        }
        catch (error) {
            console.warn(`Error retrieving tool filter for tool ${toolId} in state ${stateId} (environment: ${environment}):`, error);
            return null;
        }
    }
    //#endregion
    //#region Delete Methods
    async deleteAgentConfiguration(stateId, environment = AgentState.DEVELOPMENT) {
        const uri = `/agent/config/${stateId}/${environment}`;
        // Deleting the DEV agent configuration and the state is deleted
        // Not covering the PUBLISHED scenario
        try {
            const responseCommand = await this.MessagingHubService.processCommand({
                method: 'delete',
                to: 'postmaster@aiplatform.msging.net',
                uri: uri,
            });
            if (responseCommand.status === 'failure' || (responseCommand.status === 'success' && Object.keys(responseCommand.resource).length === 0)) {
                return false;
            }
            return true;
        }
        catch (error) {
            console.warn('Error deleting agent configuration:', error);
            return false;
        }
    }
    async deleteToolsConfiguration(stateId, applicationTenantId, environment = AgentState.DEVELOPMENT) {
        const uri = `/mcr/tools/${applicationTenantId}/${stateId}/${environment}`;
        try {
            const responseCommand = await this.MessagingHubService.processCommand({
                method: 'delete',
                to: 'postmaster@aiplatform.msging.net',
                uri: uri,
            });
            if (responseCommand.status === 'failure' || (responseCommand.status === 'success' && Object.keys(responseCommand.resource).length === 0)) {
                return false;
            }
            return true;
        }
        catch (error) {
            console.warn('Error deleting tools configuration:', error);
            return false;
        }
    }
    async validateContractStatus(applicationTenantId) {
        let isAiAgentEnabled = await ListApplicationFeatures.isAiAgentEnabled();
        if (!shouldCheckAiAgentPlan) {
            return isAiAgentEnabled;
        }
        const uri = `/contract-status/validate/${applicationTenantId}`;
        try {
            const responseCommand = await this.BlipService.processCommand({
                method: 'get',
                to: 'postmaster@aiagents.msging.net',
                uri: uri,
            });
            if (responseCommand.status === 'failure' || (Object.keys(responseCommand.resource).length === 0)) {
                return false;
            }
            return responseCommand.resource.hasValidPlan && isAiAgentEnabled;
        }
        catch (error) {
            console.warn('Error getting plan status:', error);
            return false;
        }
    }
    //#endregion
    //#region Publish Methods
    /**
     * Publishes the AI agent configuration on APA
     * @param state The current state of the AI agent.
     * @param environment The environment in which the agent is running.
     * @returns A promise that resolves when the publishing process is complete.
     */
    async publishAgentConfiguration(state, environment) {
        this.convertPromptConfigurationIntoLangchainFormat(state);
        await this.publishToolsConfiguration(state, environment);
        await this.publishGroundingToolsSetup(state, environment);
        await this.publishGroundingConfiguration(state, environment);
    }
    /**
     * Converts the prompt configuration within the provided state into the Langchain format.
     *
     * The method mutates the state to publish the agent configuration in the expected format.
     *
     * @param state - The current state view model containing the agent configuration.
     * @returns void. If no agent configuration is found, the function returns early.
     */
    convertPromptConfigurationIntoLangchainFormat(state) {
        const agentConfiguration = this.getFlowContent(state);
        if (!agentConfiguration) {
            return;
        }
        agentConfiguration.prompt = openAiInstructionToLangchainPrompt(agentConfiguration.prompt);
    }
    async publishToolsConfiguration(state, environment) {
        const otherEnvironment = environment === AgentState.DEVELOPMENT ? AgentState.PUBLISHED : AgentState.DEVELOPMENT;
        const configuration = this.getFlowContent(state);
        const { code: previousCode } = await this.retrieveCodeWithUUID(state, otherEnvironment);
        const { code: previousGroundingCode } = await this.retrieveGroundingCodeWithUUID(state, otherEnvironment);
        const [publishedAiAgentsActions, publishedAiAgentsGroundingActions] = await Promise.all([
            this.extractAiAgentsLocalCustomActionsToPublish(state, environment),
            this.extractAiAgentsGroundingLocalCustomActionsToPublish(state, environment),
        ]);
        if (!publishedAiAgentsActions) {
            throw new Error(`Failed to publish agent actions for state: ${state.id}`);
        }
        if (!publishedAiAgentsGroundingActions) {
            throw new Error(`Failed to publish agent grounding actions for state: ${state.id}`);
        }
        this.removeMcpResourceFromTools(configuration, previousCode);
        this.removeMcpResourceFromTools(configuration, previousGroundingCode);
        await this.removeUnusedToolsFromOtherApplications(state);
        const toolsToProcess = [
            { result: publishedAiAgentsActions, type: 'regular' },
            { result: publishedAiAgentsGroundingActions, type: 'grounding' },
        ];
        toolsToProcess.forEach(({ result }) => {
            if (result.shouldDelete) {
                this.removeMcpResourceFromTools(configuration, result.code);
            }
            else {
                this.addMcpResourceToTools(result, configuration);
            }
        });
    }
    async publishGroundingConfiguration(state, environment) {
        let hasSomeDocument = false;
        if (state.$groundingConfig) {
            await this.applyGroundingConfig(state, state.$groundingConfig);
            hasSomeDocument = state.$groundingConfig.length > 0;
            delete state.$groundingConfig;
        }
        else {
            hasSomeDocument = await this.publishLocalGroundingConfig(state);
        }
        if (hasSomeDocument) {
            this.insertGroundingMcpToAgentConfiguration(state, environment === AgentState.DEVELOPMENT ? 'construction' : 'flow');
        }
        else {
            this.removeGroundingMcpFromAgentConfiguration(state);
        }
    }
    async publishLocalGroundingConfig(state) {
        let hasAnyActiveDocuments = false;
        const activeCatalogs = (await this.getCatalogs(state.id, AgentState.DEVELOPMENT))
            .filter(catalog => catalog.status === groundingStatus.ACTIVE);
        for (const catalog of activeCatalogs) {
            const allDocuments = await this.getCatalogDocuments(catalog.id, state.id, AgentState.DEVELOPMENT);
            const inactiveDocuments = this.filterGrounding(allDocuments ?? [], groundingStatus.INACTIVE);
            if (!hasAnyActiveDocuments) {
                hasAnyActiveDocuments = allDocuments.length > inactiveDocuments.length;
            }
            if (inactiveDocuments && inactiveDocuments.length > 0) {
                await this.normalizeAndSetDocuments(catalog.id, inactiveDocuments, state.id, AgentState.PUBLISHED);
            }
            else {
                await this.normalizeAndSetDocuments(catalog.id, [], state.id, AgentState.PUBLISHED);
            }
        }
        this.setAgentCatalogsOnApa(activeCatalogs, state.id, AgentState.PUBLISHED);
        return hasAnyActiveDocuments;
    }
    async applyGroundingConfig(state, catalogsToApply) {
        await this.setAgentCatalogsOnApa(catalogsToApply, state.id, AgentState.DEVELOPMENT);
        for (const catalogConfig of catalogsToApply) {
            if (catalogConfig.status !== groundingStatus.ACTIVE) {
                continue;
            }
            await this
                .normalizeAndSetDocuments(catalogConfig.id, catalogConfig.excluded_documents.map(document => ({ ...document, status: groundingStatus.INACTIVE })), state.id, AgentState.DEVELOPMENT);
        }
    }
    async removeUnusedToolsFromOtherApplications(state) {
        const currentDevConfiguration = this.getFlowContent(state);
        const currentApplication = (await this.getApplication()).shortName;
        Object.keys(currentDevConfiguration.tools).forEach(async (toolCode) => {
            if (toolCode === AiAgentConstants.DEFAULT_GROUNDING_MCP || toolCode.includes(currentApplication) || toolCode.includes(CUSTOM_MCP_TOOL_PREFIX)) {
                return;
            }
            try {
                await this.deleteToolsConfiguration(state.id, this.getTenantId(), AgentState.DEVELOPMENT);
                await this.deleteToolsConfiguration(state.id, this.getTenantId(), AgentState.PUBLISHED);
            }
            catch (error) {
                console.warn('Error retrieving applications for removing unused tools:', error);
                return;
            }
            this.removeMcpResourceFromTools(currentDevConfiguration, toolCode);
        });
    }
    async publishGroundingToolsSetup(state, environment) {
        const actions = (state.$localCustomActions ?? []).filter(action => action.type === ActionType.KnowledgeBaseConsult);
        if (actions.length === 0) {
            return;
        }
        for (const action of actions) {
            const groundingToolSetup = await this.getAgentGroundingToolSetup(state.id, action.$id, AgentState.DEVELOPMENT);
            if (groundingToolSetup) {
                await this.setAgentGroundingToolSetup(state.id, action.$id, groundingToolSetup.top_k, groundingToolSetup.catalogs, groundingToolSetup.documents, environment);
            }
        }
    }
}
BuilderAiAgentService.isNewModelsEnabledPromise = null;
